/*
 * Copyright (c) 2025 Ping Identity Corporation. All rights reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */
import { actionTypes } from './request-mware.derived.js';
/**
 * @function middlewareWrapper - A "Node" and "Redux" style middleware that is called just before
 * the request is made from the SDK. This allows you access to the request for modification.
 * @param request - A request object container of the URL and the Request Init object
 * @param action - The action object that is passed into the middleware communicating intent
 * @param action.type - A "Redux" style type that contains the serialized action
 * @param action.payload - The payload of the action that can contain metadata
 * @returns {function} - Function that takes middleware parameter & runs middleware against request
 */
export function middlewareWrapper(request, 
// eslint-disable-next-line
{ type, payload }) {
    // no mutation and no reassignment
    const actionCopy = Object.freeze({ type, payload });
    return (middleware) => {
        if (!Array.isArray(middleware)) {
            return request;
        }
        // Copy middleware so the `shift` below doesn't mutate source
        const mwareCopy = middleware.map((fn) => fn);
        function iterator() {
            const nextMiddlewareToBeCalled = mwareCopy.shift();
            if (nextMiddlewareToBeCalled)
                nextMiddlewareToBeCalled(request, actionCopy, iterator);
            return request;
        }
        return iterator();
    };
}
/**
 * @function initQuery - Initializes a query object with the provided request object
 * @param {FetchArgs} fetchArgs - The request object to initialize the query with
 * @param {string} endpoint - The endpoint to be used for the query
 * @returns
 */
export function initQuery(fetchArgs, endpoint, payload) {
    let modifiedRequest = {
        ...fetchArgs,
        url: new URL(fetchArgs.url),
        headers: new Headers(fetchArgs.headers),
    };
    const queryApi = {
        applyMiddleware(middleware) {
            // Iterates and executes provided middleware functions
            // Allow middleware to mutate `request` argument
            const runMiddleware = middlewareWrapper(modifiedRequest, {
                type: actionTypes[endpoint],
                payload: payload || {},
            });
            modifiedRequest = runMiddleware(middleware);
            return queryApi;
        },
        async applyQuery(callback) {
            return await callback({ ...modifiedRequest, url: modifiedRequest.url.toString() });
        },
    };
    return queryApi;
}
